<!-- CardFieldHint — 작업지시서 생성 폼 라벨 옆 "?" 툴팁.
     이 폼 값이 실물 인증서 카드(뒷면)의 어느 칸에 인쇄되는지, 인쇄 모달과 동일한
     참조 이미지(card-back-reference.png) 위에 하이라이트로 보여준다.
     좌표 근거: CertCardPrintModal.vue PRINT_BACK_LAYOUT (라벨 박스 행 상단
     23.0/37.5/52.0/66.2%, 행 높이 ~10.6%, 좌/우 컬럼 분할) — 값 텍스트 좌표가 아니라
     라벨+값 셀 전체를 덮는 안내용 사각형이므로 여기 상수는 인쇄 좌표와 독립이다. -->
<template>
  <a-popover
    trigger="hover"
    placement="rightTop"
    overlay-class-name="card-field-hint-popover"
  >
    <template #content>
      <div class="cfh-content">
        <div class="cfh-desc">{{ $t(`workorder_card_hint_${field}`) }}</div>
        <div class="cfh-preview">
          <img :src="cardBackReferenceImg" alt="card back" class="cfh-image" />
          <span
            v-for="(box, i) in highlightBoxes"
            :key="`cfh-box-${i}`"
            class="cfh-highlight"
            :class="{ 'cfh-highlight--multi': highlightBoxes.length > 1 }"
            :style="box.style"
          >
            <span v-if="box.tag" class="cfh-tag">{{ box.tag }}</span>
          </span>
        </div>
        <div class="cfh-caption">{{ $t(captionKey) }}</div>
      </div>
    </template>
    <question-circle-outlined class="cfh-icon" />
  </a-popover>
</template>

<script>
import { QuestionCircleOutlined } from '@ant-design/icons-vue';
import cardBackReferenceImg from '@/assets/img/certCard/card-back-reference.png';

// 카드 뒷면 라벨 박스 셀 영역(% — 이미지 전체 기준). 행 top은 박스 상단, height는 박스 높이.
const ROW_TOP = { r1: 23.0, r2: 37.5, r3: 52.0, r4: 66.2 };
const ROW_HEIGHT = 10.6;
const COL = {
  L: { leftPct: 3.5, widthPct: 45 },
  R: { leftPct: 51, widthPct: 45.5 },
};
const HINT_RECTS = {
  patientName: { row: 'r1', col: 'L' },
  toothPosition: { row: 'r1', col: 'R' },
  doctorName: { row: 'r2', col: 'L' },
  orderNo: { row: 'r2', col: 'R' },
  date: { row: 'r3', col: 'L' },
  labName: { row: 'r3', col: 'R' },
  hospitalName: { row: 'r4', col: 'L' },
};

// 카드지 기인쇄 값(GTIN·No.) — 라벨 박스 그리드 밖이라 절대좌표로 지정한다.
// 좌표 근거: CertCardPrintModal.vue PRINT_BACK_LAYOUT
//   gtin      { topPct: 9.4, rightPct: 5, widthPct: 27 }  → 우상단
//   randomNo  { bottomPct: 7.3, leftPct: 50(중앙) }        → 하단 중앙
// 이 두 값은 우리가 인쇄하지 않는다(카드지에 이미 찍혀 나옴) — 위치 안내 전용.
const PREPRINTED_RECTS = {
  gtin: { topPct: 8.0, leftPct: 68, widthPct: 27, heightPct: 6.5, tag: 'GTIN' },
  randomNo: { topPct: 85.5, leftPct: 37, widthPct: 26, heightPct: 7.2, tag: 'No.' },
};

// 여러 칸을 한 번에 보여주는 복합 힌트 — 「产品选择」은 카드 1장을 고르는 행위라
// 그 카드의 식별자 2종(난수·GTIN) 위치를 함께 안내한다.
const MULTI_HINTS = {
  cardIdentity: ['randomNo', 'gtin'],
};

export default {
  name: 'CardFieldHint',

  components: { QuestionCircleOutlined },

  props: {
    // HINT_RECTS 키 중 하나 — print-data 7필드 키와 동일한 이름을 쓴다
    field: { type: String, required: true },
  },

  data() {
    return { cardBackReferenceImg };
  },

  computed: {
    // 기인쇄 값은 우리가 찍지 않으므로 "여기에 인쇄됩니다" 문구가 맞지 않는다 — 캡션을 분리한다.
    captionKey() {
      return this.isPreprintedField
        ? 'workorder_card_hint_preprinted_common'
        : 'workorder_card_hint_common';
    },

    isPreprintedField() {
      if (PREPRINTED_RECTS[this.field]) return true;
      const keys = MULTI_HINTS[this.field];
      return Array.isArray(keys) && keys.every((k) => PREPRINTED_RECTS[k]);
    },

    highlightBoxes() {
      const keys = MULTI_HINTS[this.field] || [this.field];
      return keys.map((key) => this.buildBox(key)).filter(Boolean);
    },
  },

  methods: {
    buildBox(key) {
      const pre = PREPRINTED_RECTS[key];
      if (pre) {
        return {
          tag: pre.tag,
          style: {
            top: `${pre.topPct}%`,
            left: `${pre.leftPct}%`,
            width: `${pre.widthPct}%`,
            height: `${pre.heightPct}%`,
          },
        };
      }
      const rect = HINT_RECTS[key];
      if (!rect) return null;
      const col = COL[rect.col];
      // 셀 테두리가 하이라이트 선에 딱 붙지 않도록 상하 1%씩 여유
      return {
        tag: '',
        style: {
          top: `${ROW_TOP[rect.row] - 1}%`,
          left: `${col.leftPct}%`,
          width: `${col.widthPct}%`,
          height: `${ROW_HEIGHT + 2}%`,
        },
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.cfh-icon {
  color: #8d939a;
  font-size: 14px;
  cursor: help;
  margin-left: 4px;

  &:hover {
    color: #278aec;
  }
}

.cfh-content {
  width: 300px;

  .cfh-desc {
    font-size: 13px;
    color: #2f3337;
    margin-bottom: 10px;
    line-height: 1.5;
    white-space: pre-line;
  }

  .cfh-preview {
    position: relative;
    width: 100%;
    border-radius: 6px;
    overflow: hidden;
    border: 1px solid #e8ebed;

    .cfh-image {
      display: block;
      width: 100%;
    }

    .cfh-highlight {
      position: absolute;
      border: 2px solid #fb574b;
      border-radius: 4px;
      background: rgba(251, 87, 75, 0.12);
      box-shadow: 0 0 0 2000px rgba(23, 25, 28, 0.18);
      pointer-events: none;
    }

    // 하이라이트가 2개 이상이면 각 박스의 스프레드 그림자가 서로를 덮어 어둡게 만든다 —
    // 다중일 때는 딤 처리를 끄고 테두리+채움만으로 구분한다.
    .cfh-highlight--multi {
      box-shadow: none;
    }

    .cfh-tag {
      position: absolute;
      top: -9px;
      left: -2px;
      padding: 0 4px;
      background: #fb574b;
      color: #fff;
      font-size: 9px;
      line-height: 12px;
      border-radius: 3px;
      white-space: nowrap;
    }
  }

  .cfh-caption {
    margin-top: 8px;
    font-size: 12px;
    color: #8d939a;
    line-height: 1.4;
  }
}
</style>
